import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";

const HOLD_MS = 400;

// Tap = toggle recording; press & hold = record until release
export const MicButton = ({ dictation, onHoldChange }) => {
  const { status, start, stop } = dictation;
  const statusRef = useRef(status);
  statusRef.current = status;
  const press = useRef(null);
  const [holding, setHolding] = useState(false);
  const busy = status === "requesting" || status === "transcribing";
  const recording = status === "recording";

  const setHold = (v) => { setHolding(v); onHoldChange?.(v); };

  const onDown = (e) => {
    if (busy || (e.pointerType === "mouse" && e.button !== 0)) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* pointer no longer active */ }
    if (recording) { press.current = null; stop(); return; }
    press.current = { at: Date.now() };
    start();
    // Engage hold once recording is live (allow slow mic start; a permission prompt >1s means tap mode)
    const check = () => {
      const p = press.current;
      if (!p) return;
      if (statusRef.current === "recording") { p.held = true; setHold(true); return; }
      if (Date.now() - p.at < 1000) p.timer = setTimeout(check, 100);
    };
    press.current.timer = setTimeout(check, HOLD_MS);
  };

  const onUp = () => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    clearTimeout(p.timer);
    if (p.held && statusRef.current === "recording") stop();
    setHold(false);
  };

  const onKey = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (busy || e.repeat) return;
    recording ? stop() : start();
  };

  return (
    <button
      type="button"
      data-testid="mic-toggle-btn"
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onKeyDown={onKey}
      onContextMenu={(e) => e.preventDefault()}
      aria-disabled={busy}
      aria-label={recording ? "Stop dictation" : "Start dictation (tap, or press and hold)"}
      title={recording ? "Stop dictation" : "Tap to dictate, or press and hold to talk"}
      style={{ touchAction: "none", WebkitTouchCallout: "none" }}
      className={`absolute top-3 right-3 rounded-full flex items-center justify-center select-none transition-[transform,background-color] duration-150 aria-disabled:opacity-70 aria-disabled:cursor-wait ${
        holding ? "h-14 w-14 -top-0.5 -right-0.5 bg-red-500 text-white scale-110 pulse-glow"
          : recording ? "h-10 w-10 bg-red-500 text-white pulse-glow"
          : "h-10 w-10 bg-[#22C55E] text-[#0F1117] hover:scale-110"
      }`}
    >
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : recording && !holding ? <Square className="h-4 w-4" fill="currentColor" /> : <Mic className="h-5 w-5" />}
    </button>
  );
};
